import React from 'react';
import styled from 'styled-components';
import { Link } from 'gatsby';
import slugify from 'slugify';
import { config } from '@keytrade/functions';
import { Badge } from '@keytrade/components-badge';

import { mediaQuery } from '@/utils/helpers';
import useTranslations from '@/hooks/useTranslations';
import { useLocalization } from '@/../plugins/keytrade-localization/useLocalization';

import SeminarDate from './SeminarDate';

const { list: colors } = config.colors;
const { getFont } = config.fonts;

const SeminarBlock = styled(Link)`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${colors.Grey};
  text-decoration: none;
  padding 16px 0;
  &:last-of-type
  {
    border-bottom: none;
  }

  &:hover
  {
    background-color: ${colors.BlueSmoke};
  }
  
  ${mediaQuery.md(`
    width: 100%;
    padding: 20px;
    border-left: 1px solid ${colors.Grey};
    border-right: 1px solid ${colors.Grey};
    &:first-of-type {
      border-top: 1px solid ${colors.Grey};
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
    }
    &:last-of-type
    {
      border-bottom: 1px solid ${colors.Grey};
      border-bottom-left-radius: 12px;
      border-bottom-right-radius: 12px;
    }
  `)}
`;

const SeminarInformationWrapper = styled.div`
  display: flex;
  ${mediaQuery.md(`
    align-items: center;
    margin-bottom: 0;
  `)}
`;

const SeminarInformaton = styled.div`
  margin-left: 11px;
  display: flex;
  flex-direction: column;
  width: 100%;
  ${mediaQuery.md(`
    margin-left: 20px;
  `)}
`;

const SeminarTitle = styled.span`
  display: block;
  font-size: 16px;
  line-height: 24px;
  font-family: ${({ theme }) => getFont(theme, 'semiBold')};
  color: ${colors.BlueDawn};
  ${mediaQuery.md(`
    font-size: 18px;
    line-height: 28px;
    max-width: 70%;
  `)}
`;
const SeminarDateWrapper = styled.div``;

const SeminarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const SeminarLocationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  line-height: 20px;
  text-align: right;
  font-family: ${({ theme }) => getFont(theme, 'regular')};
  color: ${colors.BlueMoon};
`;
const SeminarTimeSlot = styled.span`
  margin-left: 5px;
`;
const SeminarLocation = styled.div`
  display: flex;
  align-items: center;
`;
const SeminarLanguage = styled.span`
  display: none;
  ${mediaQuery.md(`
    display: block;
  `)}
`;
const SeminarTopics = styled.div`
  display: none;
  width: 30%;
  flex-wrap: wrap;
  justify-content: flex-end;
  line-height: 28px;
  ${mediaQuery.md(`
    display: flex;
  `)}
`;

const TopicWrapper = styled.div`
  margin-left: 8px;
  &:last-of-type {
    margin-right: 0;
  }
`;

const LocationName = styled.span`
  margin-right: 5px;
`;

const SeminarName = styled.span`
  margin-right: 10px;
`;

export type SeminarType = {
  id: string;
  name: string;
  type: string;
  lang: string;
  private: boolean;
  topics: [string];
  datetime: {
    start: string;
    end: string;
    date: string;
  };
  location: {
    name: string;
  };
  jsonId: string;
  full: boolean;
};

const Seminar: React.FC<SeminarType> = ({
  name,
  datetime,
  location,
  lang,
  type,
  topics,
  jsonId,
  full,
}) => {
  const { locale } = useLocalization();
  const { t } = useTranslations();

  return (
    <SeminarBlock
      to={`/${locale}/${t('seminars.sessionPath')}/${slugify(name, {
        lower: true,
        strict: true,
      })}-${jsonId}`}
    >
      <SeminarInformationWrapper>
        <SeminarDateWrapper>
          <SeminarDate datetime={datetime} locale={locale} />
        </SeminarDateWrapper>
        <SeminarInformaton>
          <SeminarContent>
            <SeminarTitle>
              <SeminarName>{name}</SeminarName>
              {full && <Badge kind='light'>{t('seminars.full')}</Badge>}
            </SeminarTitle>

            <SeminarTopics>
              {topics &&
                React.Children.toArray(
                  topics.map((topic, key) => {
                    return (
                      <TopicWrapper key={key}>
                        <Badge kind='light' size='xs'>
                          {t(`seminars.topic.${topic}`)}
                        </Badge>
                      </TopicWrapper>
                    );
                  }),
                )}
            </SeminarTopics>
          </SeminarContent>
          <SeminarLocationWrapper>
            <SeminarLocation>
              <LocationName>
                {type === 'webinar' ? t('seminars.webinar') : location.name}
              </LocationName>
              <span>&#8226;</span>
              <SeminarTimeSlot>
                {datetime.start} - {datetime.end}
              </SeminarTimeSlot>
            </SeminarLocation>
            <SeminarLanguage>{t(lang)}</SeminarLanguage>
          </SeminarLocationWrapper>
        </SeminarInformaton>
      </SeminarInformationWrapper>
    </SeminarBlock>
  );
};

export default Seminar;
